import { TextFieldWidget } from '@tableau/widgets';
import React from 'react';
import { CSSProperties } from 'react';

import { ReactActionDialogBodyProps } from './ReactActionDialog';

export type ActionDialogBodyWithTextInputProps = {
  readonly originalText: string;
  readonly label: string;
  readonly maxLength: number;
}

export type ActionDialogBodyWithTextInputState = {
  readonly text: string;
  readonly errorMessage?: string;
}

const ZERO_WIDTH_SPACE = '\u200B';
const FullWidthInputStyle: CSSProperties = { width: '100%' };

export function ActionDialogBodyWithTextInput(
  props: ReactActionDialogBodyProps<ActionDialogBodyWithTextInputProps, ActionDialogBodyWithTextInputState>
): JSX.Element {
  // The state of isComposing will be true if we are using a keyboard
  // that adds native UI to show suggestions (e.g. Japanese keyboard, Hiragana).
  // See Shared Widgets documentation for most up-to-date info regarding how
  // they pass this value from the widget.
  const [isComposing, setIsComposing] = React.useState<boolean>(false);

  const { doAction, maxLength, updateDialogState } = props;

  const onChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement> & { isComposing: boolean }) => {
      const newText = e.target.value.substring(0, maxLength);
      updateDialogState({ errorMessage: undefined, text: newText });
      setIsComposing(e.isComposing);
    },
    [maxLength, updateDialogState]
  );

  const handleEnterKey = React.useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isComposing) {
        doAction();
      }
    },
    [doAction, isComposing]
  );

  return (
    <TextFieldWidget
      className='tb-dialog-body-input'
      style={FullWidthInputStyle}
      kind='outline'
      label={props.label}
      maxLength={props.maxLength}
      message={props.dialogState.errorMessage || ZERO_WIDTH_SPACE}
      value={props.dialogState.text}
      onChange={onChange}
      onKeyDown={handleEnterKey}
      disabled={props.actionBusy}
      valid={props.dialogState.errorMessage ? false : undefined}
      testId={ActionDialogInputTestId}
    />
  );
}

export const ActionDialogInputTestId = 'confirm-action-dialog-input-text-box';
